"use strict";
/**
 * Document Validation (GH#21)
 *
 * Pure validation functions for document fields. Mirrors the pattern of
 * `rfc.validation.ts`, `email.validation.ts`, etc. — returns
 * `ValidationResult` so callers (Value Objects in backend, frontend Zod
 * schemas, migration scripts) all share one source of truth.
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.validateDocumentSlug = validateDocumentSlug;
exports.slugifyTitle = slugifyTitle;
exports.validateVersionNumber = validateVersionNumber;
exports.parseVersionNumber = parseVersionNumber;
// ─── Slug ───────────────────────────────────────────────────────────────────
const SLUG_PATTERN = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;
const SLUG_MIN_LENGTH = 3;
const SLUG_MAX_LENGTH = 80;
/**
 * Validates a document slug.
 *
 * Rules:
 * - 3–80 chars
 * - lowercase alphanumeric + hyphens only
 * - no leading/trailing/consecutive hyphens
 *
 * @example
 *   validateDocumentSlug("protocolo-ego")        // { isValid: true }
 *   validateDocumentSlug("Protocolo EGO")        // { isValid: false, error: ... }
 *   validateDocumentSlug("ab")                    // { isValid: false, error: ... }
 *   validateDocumentSlug("foo--bar")              // { isValid: false, error: ... }
 */
function validateDocumentSlug(value) {
    if (!value || !value.trim()) {
        return { isValid: false, error: "El slug es requerido" };
    }
    const trimmed = value.trim();
    if (trimmed.length < SLUG_MIN_LENGTH) {
        return {
            isValid: false,
            error: `El slug debe tener al menos ${SLUG_MIN_LENGTH} caracteres`,
        };
    }
    if (trimmed.length > SLUG_MAX_LENGTH) {
        return {
            isValid: false,
            error: `El slug no puede exceder ${SLUG_MAX_LENGTH} caracteres`,
        };
    }
    if (!SLUG_PATTERN.test(trimmed)) {
        return {
            isValid: false,
            error: "El slug solo puede contener minúsculas, números y guiones (sin espacios ni guiones consecutivos)",
        };
    }
    return { isValid: true };
}
/**
 * Generates a slug candidate from arbitrary text. Caller is responsible for
 * resolving collisions (typically by appending `-2`, `-3`, ...).
 *
 * NOTE: this is a best-effort transliteration. Spanish accents are mapped to
 * their unaccented form. Other unicode is dropped.
 */
function slugifyTitle(title) {
    return title
        .toLowerCase()
        .normalize("NFD")
        .replace(/[̀-ͯ]/g, "") // strip accents
        .replace(/ñ/g, "n")
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/^-+|-+$/g, "")
        .slice(0, SLUG_MAX_LENGTH)
        .replace(/-+$/g, ""); // re-trim if slice landed on a hyphen
}
// ─── Version Number ─────────────────────────────────────────────────────────
const VERSION_PATTERN = /^\d+\.\d+\.\d+$/;
/**
 * Validates a semver-style version number `major.minor.patch`.
 *
 * @example
 *   validateVersionNumber("1.0.0")     // { isValid: true }
 *   validateVersionNumber("1.0")        // { isValid: false }
 *   validateVersionNumber("v1.0.0")     // { isValid: false }
 */
function validateVersionNumber(value) {
    if (!value || !value.trim()) {
        return { isValid: false, error: "El número de versión es requerido" };
    }
    if (!VERSION_PATTERN.test(value.trim())) {
        return {
            isValid: false,
            error: "Versión inválida. Formato esperado: MAJOR.MINOR.PATCH (ej. 1.0.0)",
        };
    }
    return { isValid: true };
}
/**
 * Parses a validated version string into its three numeric components.
 * Throws if the input is invalid — call `validateVersionNumber` first.
 */
function parseVersionNumber(value) {
    const result = validateVersionNumber(value);
    if (!result.isValid) {
        throw new Error(result.error ?? "Versión inválida");
    }
    const [major, minor, patch] = value.trim().split(".").map(Number);
    return { major, minor, patch };
}
